import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Settings, Bell, Lock, Save } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import UserProfile from '@components/users/UserProfile';
import Button from '@components/common/Button';

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'My Profile - RDX Ticketing System';
  }, []);

  const tabs = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      description: 'Personal information'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Notification preferences'
    },
    {
      id: 'security',
      label: 'Security',
      icon: Lock,
      description: 'Password and security'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.32,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  // Inline style objects to match Dashboard visual language
  const styles = {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    hero: {
      borderRadius: '1rem',
      padding: '1.25rem',
      color: 'white',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(2,6,23,0.08)'
    },
    heroInner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    heroLeft: {
      flex: 1,
      minWidth: 0
    },
    heroTitle: {
      fontSize: '1.375rem',
      fontWeight: 700,
      margin: 0,
      color: 'white',
      lineHeight: 1.1
    },
    heroSub: {
      marginTop: '0.375rem',
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    heroRight: {
      textAlign: 'right',
      minWidth: '11rem'
    },
    dateText: {
      margin: 0,
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    metaText: {
      marginTop: '0.25rem',
      fontSize: '0.8125rem',
      color: 'rgba(255,255,255,0.85)',
      textTransform: 'capitalize'
    },
    cardWrapper: {
      backgroundColor: 'transparent'
    },
    tabsWrapper: {
      background: '#fff',
      borderRadius: '1rem',
      padding: '1.5rem',
      boxShadow: '0 8px 24px rgba(2,6,23,0.04)',
      border: '1px solid rgba(14,20,25,0.04)'
    },
    tabsList: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1.5rem',
      borderBottom: '1px solid rgba(14,20,25,0.04)',
      paddingBottom: '1rem'
    },
    tab: (isActive) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: isActive ? 'var(--primary-50)' : 'transparent',
      color: isActive ? 'var(--primary-700)' : 'var(--gray-600)',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s'
    })
  };

  // gradient background for profile (emerald theme)
  const heroGradient = {
    background: 'linear-gradient(90deg, #059669, #047857)'
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <UserProfile />;
      case 'notifications':
        return <NotificationPreferences />;
      case 'security':
        return <SecuritySettings />;
      default:
        return <UserProfile />;
    }
  };

  return (
    <motion.div
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Profile Hero */}
      <motion.div variants={itemVariants}>
        <div style={{ ...styles.hero, ...heroGradient }}>
          <div style={styles.heroInner}>
            <div style={styles.heroLeft}>
              <h1 style={styles.heroTitle}>
                <User style={{ display: 'inline-block', width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' }} />
                                My Profile
              </h1>
              <p style={styles.heroSub}>
                                Manage your account settings, preferences, and security options.
              </p>
            </div>

            <div style={styles.heroRight} className="hidden-md">
              <p style={styles.dateText}>
                {user?.first_name} {user?.last_name}
              </p>
              <p style={styles.metaText}>
                {user?.role ? `${user.role}` : '—'} {user?.department ? `• ${user.department}` : ''}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Profile Content */}
      <motion.div variants={itemVariants}>
        <div style={styles.tabsWrapper}>
          {/* Tabs */}
          <div style={styles.tabsList}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
                            
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={styles.tab(isActive)}
                >
                  <Icon style={{ width: 16, height: 16 }} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Notification Preferences Component
const NotificationPreferences = () => {
  const [preferences, setPreferences] = useState({
    email: {
      ticket_created: true,
      ticket_assigned: true,
      ticket_updated: false,
      ticket_commented: true,
      ticket_closed: true,
      sla_breach: true,
      daily_digest: false,
      weekly_digest: true
    },
    in_app: {
      ticket_created: true,
      ticket_assigned: true,
      ticket_updated: true,
      ticket_commented: true,
      ticket_closed: true,
      sla_breach: true
    }
  });

  const handlePreferenceChange = (type, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [key]: value
      }
    }));
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Notification Preferences</h2>

      <div className="space-y-8">
        {/* Email Notifications */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="w-4 h-4" />
                        Email Notifications
          </h3>
          <div className="space-y-3">
            {[
              { key: 'ticket_created', label: 'New ticket created', description: 'When tickets are assigned to me' },
              { key: 'ticket_assigned', label: 'Ticket assigned to me', description: 'When tickets are assigned to me' },
              { key: 'ticket_updated', label: 'Ticket updates', description: 'When tickets I\'m involved in are updated' },
              { key: 'ticket_commented', label: 'New comments', description: 'When someone comments on my tickets' },
              { key: 'ticket_closed', label: 'Ticket closed', description: 'When tickets are closed' },
              { key: 'sla_breach', label: 'SLA breach warnings', description: 'When SLA deadlines are approaching' },
              { key: 'daily_digest', label: 'Daily digest', description: 'Summary of daily activities' },
              { key: 'weekly_digest', label: 'Weekly digest', description: 'Weekly summary report' }
            ].map((item) => (
              <div key={item.key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={preferences.email[item.key]}
                  onChange={(e) => handlePreferenceChange('email', item.key, e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-900 cursor-pointer">
                    {item.label}
                  </label>
                  <p className="text-xs text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* In-App Notifications */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-4 h-4" />
                        In-App Notifications
          </h3>
          <div className="space-y-3">
            {[
              { key: 'ticket_created', label: 'New ticket created' },
              { key: 'ticket_assigned', label: 'Ticket assigned to me' },
              { key: 'ticket_updated', label: 'Ticket updates' },
              { key: 'ticket_commented', label: 'New comments' },
              { key: 'ticket_closed', label: 'Ticket closed' },
              { key: 'sla_breach', label: 'SLA breach warnings' }
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <label className="text-sm font-medium text-gray-900">
                  {item.label}
                </label>
                <input
                  type="checkbox"
                  checked={preferences.in_app[item.key]}
                  onChange={(e) => handlePreferenceChange('in_app', item.key, e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button leftIcon={<Save className="w-4 h-4" />}>
                        Save Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

// Security Settings Component
const SecuritySettings = () => {
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [errors, setErrors] = useState({});

  const handlePasswordChange = (field, value) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Password change logic
  };

  return (
    <div className="space-y-6">
      {/* Change Password */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Change Password</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password
            </label>
            <input
              type="password"
              value={passwords.current}
              onChange={(e) => handlePasswordChange('current', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password
            </label>
            <input
              type="password"
              value={passwords.new}
              onChange={(e) => handlePasswordChange('new', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password
            </label>
            <input
              type="password"
              value={passwords.confirm}
              onChange={(e) => handlePasswordChange('confirm', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <Button type="submit" leftIcon={<Save className="w-4 h-4" />}>
                        Update Password
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;